import { useState, useCallback } from 'react';
import { socApiService } from '../api/socApiService';
import { UseSOCApiReturn, UseSOCApiState, Job, SOCOccupation } from '../types';

export function useSOCApi(): UseSOCApiReturn {
  const [state, setState] = useState<UseSOCApiState>({
    jobs: [],
    loading: false,
    error: null,
    totalJobs: 0,
  });

  const setLoading = useCallback((loading: boolean) => {
    setState(prev => ({ ...prev, loading }));
  }, []);

  const setError = useCallback((error: string | null) => {
    setState(prev => ({ ...prev, error }));
  }, []);

  const setJobs = useCallback((occupations: SOCOccupation[], total: number) => {
    const jobs = occupations.map((occupation, index) => 
      socApiService.transformToJob(occupation, index)
    );
    setState(prev => ({ 
      ...prev, 
      jobs, 
      totalJobs: total
    }));
  }, []);

  // Main function for server-side pagination
  const loadJobs = useCallback(async ({ limit, skip, sector, career_cluster, search, sort_by, min_current_jobs }: { limit: number; skip: number; sector?: string; career_cluster?: string; search?: string; sort_by?: string; min_current_jobs?: number }) => {
    setLoading(true);
    setError(null);
    try {
      let response;
      
      // Build parameters object for combined filters
      const params: any = { limit, skip };
      
      if (sector && sector !== 'all') {
        params.sector = sector;
      }
      
      if (career_cluster && career_cluster !== 'all') {
        params.career_cluster = career_cluster;
      }
      
      if (sort_by && sort_by !== 'default') {
        params.sort_by = sort_by;
      }
      if (typeof min_current_jobs === 'number') {
        params.min_current_jobs = min_current_jobs;
      }
      
      // If search is provided, use search endpoint (search takes priority)
      if (search) {
        response = await socApiService.searchOccupations(search, limit, skip, sort_by, min_current_jobs);
        if (process.env.NODE_ENV !== 'production') {
          console.log('RAW SEARCH ROW EXAMPLE', response.data?.[0]);
        }
      } else {
        // Use combined filters through the main occupations endpoint
        response = await socApiService.getOccupations(params);
      }

      const percentileFields: Array<keyof SOCOccupation> = [
        "2023 Pct. 10 Annual Earnings_Lightcast",
        "2023 Pct. 90 Annual Earnings_Lightcast",
        "2023 Pct. 10 Hourly Earnings_Lightcast",
        "2023 Pct. 90 Hourly Earnings_Lightcast"
      ];

      let occupations = response.data || [];

      if (search && occupations.length > 0) {
        occupations = await Promise.all(
          occupations.map(async (occupation) => {
            const missingPercentile = percentileFields.some((field) => occupation[field] === undefined || occupation[field] === null);
            if (!missingPercentile) {
              return occupation;
            }

            try {
              const detailed = await socApiService.getOccupationByCode(occupation.SOC_Code);
              if (detailed) {
                const enriched: SOCOccupation = { ...occupation };
                const detailedRecord = detailed as Partial<SOCOccupation>;
                const enrichedRecord = enriched as unknown as Record<string, unknown>;
                percentileFields.forEach((field) => {
                  const fieldValue = detailedRecord[field];
                  if (
                    (enriched[field] === undefined || enriched[field] === null) &&
                    fieldValue !== undefined &&
                    fieldValue !== null
                  ) {
                    enrichedRecord[field] = fieldValue;
                  }
                });
                return enriched;
              }
            } catch (error) {
              if (process.env.NODE_ENV !== 'production') {
                console.warn(`Failed to enrich occupation ${occupation.SOC_Code}`, error);
              }
            }
            return occupation;
          })
        );
      }

      setJobs(occupations, response.total || 0);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load jobs');
    } finally {
      setLoading(false);
    }
  }, [setLoading, setError, setJobs]);

  const clearError = useCallback(() => {
    setError(null);
  }, [setError]);

  return {
    ...state,
    loadJobs,
    clearError,
  };
} 